import React, { useState } from 'react';
import { Device } from '../../lib/types';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

interface DeviceReturnedModalProps {
  open: boolean;
  /** Dismiss the flow entirely (X / backdrop / Escape). */
  onClose: () => void;
  /** Return to the previous step — the cancel-reason picker. */
  onBack: () => void;
  device: Device | null;
  /** Recovers the device (unlinks the pet, returns it to stock). */
  onConfirm: (device: Device) => Promise<void>;
}

/**
 * Intermediate confirmation shown after the rescue picks "Adopter kept the pet
 * but returned the device" in the cancel-reason modal. It spells out what
 * confirming does (device unlinks from the pet and returns to stock; the pet
 * stays with the adopter) before the transfer is actually recovered.
 */
export default function DeviceReturnedModal({ open, onClose, onBack, device, onConfirm }: DeviceReturnedModalProps) {
  const [submitting, setSubmitting] = useState(false);

  if (!device) {
    return null;
  }

  const currentDevice = device;

  async function handleConfirm() {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      // Await the mutation so a failure keeps the modal open for retry — the
      // error toast is raised by the handler in Devices, which re-throws.
      await onConfirm(currentDevice);
      onClose();
    } catch {
      // Kept open on failure; toast already surfaced the error.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose}>
      <ModalTitle>Return device to stock</ModalTitle>
      <p className={styles.modalDescription}>
        You picked <b>&ldquo;Adopter kept the pet but returned the device.&rdquo;</b> Here&rsquo;s what happens when you
        confirm:
      </p>
      <div className={styles.outcomeBox}>
        <div className={styles.outcomeBoxIcon}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
            <path d="M3 8l9 5 9-5" />
            <path d="M12 13v8" />
          </svg>
        </div>
        <div>
          <div className={styles.outcomeBoxLabel}>What happens</div>
          <div className={styles.outcomeBoxText}>
            Device <b>{device.id}</b> unlinks from {device.petName ?? 'the pet'} and returns to <b>Devices in Stock</b>{' '}
            with no pet. {device.petName ?? 'The pet'} stays with the adopter.
          </div>
        </div>
      </div>
      <ModalActions>
        <Button variant="ghost" onClick={onBack} disabled={submitting}>
          Back
        </Button>
        <Button variant="green" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Working…' : 'Confirm — back to stock'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
